import { scaleTime } from 'd3-scale';
import { timeMonth } from 'd3-time';

import './styles/graphic.scss';
import data from './data/trades.json';
import trumpIcon from './images/trump-icon.png';

const POST_ICON = 14; // diameter of the circular post marker

// palette (from design handoff)
const BUY = '#e2662e';
const SELL = '#14766c';
const POST = '#3f7fbe';
const GRID = '#efede9';
const RAIL = '#e3e0db';
const MUTED_MARK = '#d5d2cc'; // buys/sells that fall outside a post window

const DAY_MS = 24 * 60 * 60 * 1000;
const ROW_H = 56;
const ROW_PAD = 10; // top+bottom padding per row
const AXIS_H = 16;

const parseDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// AP style: spell out March–July, abbreviate the rest
const AP_MONTHS = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];

// Merge overlapping [start, end] intervals (ms).
function mergeIntervals(intervals) {
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
  const merged = [];
  sorted.forEach((iv) => {
    const last = merged[merged.length - 1];
    if (last && iv[0] <= last[1]) {
      last[1] = Math.max(last[1], iv[1]);
    } else {
      merged.push(iv.slice());
    }
  });
  return merged;
}

// Build the inner SVG markup for a single stock's timeline strip.
function rowSvg(stock, geo) {
  const {
    scale, contentW, months, windowDays,
  } = geo;
  const railY = ROW_H / 2;
  const winMs = windowDays * DAY_MS;
  const domain = scale.domain();
  const lo = +domain[0];
  const hi = +domain[1];

  // 1. month gridlines
  let svg = months
    .map(mx => `<line x1="${mx.toFixed(1)}" x2="${mx.toFixed(1)}" y1="0" y2="${ROW_H}" stroke="${GRID}" stroke-width="1"></line>`)
    .join('');

  // 2. post windows (translucent, merged)
  const wins = (stock.posts || []).map((p) => {
    const t = +parseDate(p);
    return [Math.max(lo, t - winMs), Math.min(hi, t + winMs)];
  });
  const merged = mergeIntervals(wins);
  const inWindow = t => merged.some(w => t >= w[0] && t <= w[1]);
  // only keep the blue band for windows that actually contain a trade
  const tradeTimes = [...(stock.buys || []), ...(stock.sells || [])].map(t => +parseDate(t));
  const drawWins = merged.filter(w => tradeTimes.some(t => t >= w[0] && t <= w[1]));
  // band fills the full lane height, matching the grey strip background
  const bandY = 0;
  const bandH = ROW_H;
  svg += drawWins
    .map((w) => {
      const x = scale(new Date(w[0]));
      const width = Math.max(1, scale(new Date(w[1])) - x);
      return `<rect x="${x.toFixed(1)}" y="${bandY}" width="${width.toFixed(1)}" height="${bandH}" fill="${POST}" opacity="0.12" rx="3"></rect>`;
    })
    .join('');

  // 3. center hairline
  svg += `<line x1="0" x2="${contentW}" y1="${railY}" y2="${railY}" stroke="${RAIL}" stroke-width="1"></line>`;

  // 4. posts — circular Trump icon straddling the top edge of the post window
  const r = POST_ICON / 2;
  const cy = bandY; // window top; half the icon sits above, half below
  svg += (stock.posts || [])
    .map((p) => {
      const x = scale(parseDate(p)).toFixed(1);
      return `<circle cx="${x}" cy="${cy}" r="${r}" fill="url(#ttl-trump)" stroke="#fff" stroke-width="1"></circle>`;
    })
    .join('');

  // 5. buys (lollipop above rail) — coloured only when inside a post window
  svg += (stock.buys || [])
    .map((b) => {
      const dt = parseDate(b);
      const x = scale(dt).toFixed(1);
      const color = inWindow(+dt) ? BUY : MUTED_MARK;
      return `<line x1="${x}" x2="${x}" y1="${railY}" y2="${railY - 10}" stroke="${color}" stroke-width="1.5"></line>`
        + `<path d="M0,-7 L5.6,0 L-5.6,0 Z" fill="${color}" stroke="#fff" stroke-width="1" transform="translate(${x},${railY - 10})"></path>`;
    })
    .join('');

  // 6. sells (lollipop below rail) — coloured only when inside a post window
  svg += (stock.sells || [])
    .map((s) => {
      const dt = parseDate(s);
      const x = scale(dt).toFixed(1);
      const color = inWindow(+dt) ? SELL : MUTED_MARK;
      return `<line x1="${x}" x2="${x}" y1="${railY}" y2="${railY + 10}" stroke="${color}" stroke-width="1.5"></line>`
        + `<path d="M0,7 L5.6,0 L-5.6,0 Z" fill="${color}" stroke="#fff" stroke-width="1" transform="translate(${x},${railY + 10})"></path>`;
    })
    .join('');

  return `<svg width="${contentW}" height="${ROW_H}" viewBox="0 0 ${contentW} ${ROW_H}" style="display:block; overflow:visible;">${svg}</svg>`;
}

export default function (config, container, isMobile) {
  const { initialWidth } = config;

  // guard against a malformed/placeholder data file
  if (!data || !data.dateStart || !Array.isArray(data.stocks) || !data.stocks.length) {
    container.html('<p style="font-family:\'CNN Sans Display\',\'CNN\',Helvetica,Arial,sans-serif; font-size:14px; color:#777; padding:24px;">Timeline data is unavailable. Run <code>python3 scripts/process_data.py</code> to build <code>src/data/trades.json</code>.</p>');
    if (window.pymChild) window.pymChild.sendHeight();
    return;
  }

  const stocks = data.stocks || [];
  const windowDays = data.windowDays || 7;
  const startDate = parseDate(data.dateStart);
  const endDate = parseDate(data.dateEnd);
  const viewStart = parseDate(data.viewStart || data.dateStart);
  const viewEnd = parseDate(data.viewEnd || data.dateEnd);

  // --- responsive layout -------------------------------------------------
  // initialWidth is the #graphic client width (already inside the card padding),
  // so it is the inner content width. Cap it to the card's max inner width.
  const maxInner = 1050 - 36 * 2;
  const contentW = Math.min(initialWidth, maxInner);
  const gap = isMobile ? 12 : 16;

  // size the label column to the widest company name (matches .ttl-name: 14px/700)
  const labelFont = '500 14px "CNN Sans Display", "CNN", Helvetica, Arial, sans-serif';
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  measureCtx.font = labelFont;
  const labelW = Math.ceil(
    Math.max(...stocks.map(s => measureCtx.measureText(s.name).width)),
  ) + 1;

  // The scroll viewport shows only the default date window; the full range is
  // rendered wider than the viewport so the user can scroll left/right.
  const viewportW = isMobile
    ? Math.max(140, contentW)
    : Math.max(140, contentW - labelW - gap);
  const viewDays = Math.max(1, (+viewEnd - +viewStart) / DAY_MS);
  const fullDays = Math.max(1, (+endDate - +startDate) / DAY_MS);
  const pxPerDay = viewportW / viewDays;
  const timelineW = Math.round(pxPerDay * fullDays);

  const scale = scaleTime().domain([startDate, endDate]).range([0, timelineW]);

  // month positions + label cadence (across the full scrollable width)
  const monthStarts = timeMonth.range(
    timeMonth.floor(startDate),
    timeMonth.offset(timeMonth.floor(endDate), 1),
  );
  const monthXs = monthStarts.map(d => scale(d)).filter(x => x >= -0.5 && x <= timelineW + 0.5);
  const spacing = monthXs.length > 1 ? monthXs[1] - monthXs[0] : timelineW;
  // on mobile, show every other month to reduce clutter
  const labelEvery = Math.max(1, Math.ceil(46 / Math.max(1, spacing))) * (isMobile ? 2 : 1);

  // Attach the year to the label at the default view's first month (March 2025).
  const viewMonth = +timeMonth.floor(viewStart);
  let yearShown = false;
  const monthLabels = monthStarts
    .map((d, i) => {
      if (i % labelEvery !== 0) return '';
      const x = scale(d);
      if (x < -0.5 || x > timelineW - 18) return '';
      let label = AP_MONTHS[d.getMonth()].toUpperCase();
      if (!yearShown && +d >= viewMonth) {
        label += `<span class="ttl-year">${d.getFullYear()}</span>`;
        yearShown = true;
      }
      return `<div class="ttl-month" style="left:${x.toFixed(1)}px;">${label}</div>`;
    })
    .join('');

  const geo = {
    scale, contentW: timelineW, months: monthXs, windowDays, mobile: isMobile,
  };

  const rowBlockH = ROW_H + ROW_PAD * 2;

  // --- legend swatches ---------------------------------------------------
  const legend = `
    <div class="ttl-legend">
      <span class="ttl-legend-item"><img class="ttl-legend-icon" src="${trumpIcon}" width="14" height="14" alt="">Pro-company Truth post</span>
      <span class="ttl-legend-item"><svg width="20" height="14" viewBox="0 0 20 14"><path d="M14,2 L19,11 L9,11 Z" fill="${MUTED_MARK}"></path><path d="M7,2 L12,11 L2,11 Z" fill="${BUY}"></path></svg>Bought</span>
      <span class="ttl-legend-item"><svg width="20" height="14" viewBox="0 0 20 14"><path d="M14,12 L19,3 L9,3 Z" fill="${MUTED_MARK}"></path><path d="M7,12 L12,3 L2,3 Z" fill="${SELL}"></path></svg>Sold</span>
      <span class="ttl-legend-item"><svg width="18" height="14" viewBox="0 0 18 14"><rect x="0" y="0" width="18" height="14" fill="${POST}" opacity="0.14"></rect></svg>&plusmn;${windowDays} days around a post</span>
    </div>`;

  // --- fixed left label column -------------------------------------------
  const labelCol = `
    <div class="ttl-labels" style="width:${labelW}px;">
      <div class="ttl-axis-spacer" style="height:${AXIS_H}px;"></div>
      ${stocks.map(s => `
        <div class="ttl-rowlabel" style="height:${rowBlockH}px;">
          <div class="ttl-name">${s.name}</div>
        </div>`).join('')}
    </div>`;

  // --- scrollable timeline pane ------------------------------------------
  // Desktop: strips only (names live in the fixed left column).
  // Mobile: each company name is stacked above its strip and pinned to the
  // left so it stays visible while the timeline scrolls horizontally.
  const axisHtml = `<div class="ttl-axis" style="height:${AXIS_H}px; width:${timelineW}px;">${monthLabels}</div>`;

  const strips = stocks
    .map((s, i) => {
      if (isMobile) {
        // date labels live under the first company's name, above its data
        return '<div class="ttl-strip ttl-strip--stacked">'
          + `<div class="ttl-name ttl-name--stacked">${s.name}</div>`
          + `${i === 0 ? axisHtml : ''}${rowSvg(s, geo)}</div>`;
      }
      return `<div class="ttl-strip" style="height:${rowBlockH}px;">${rowSvg(s, geo)}</div>`;
    })
    .join('');

  const scrollPane = `
    <div class="ttl-scroll" style="width:${viewportW}px;">
      <div class="ttl-scroll-inner" style="width:${timelineW}px;">
        ${isMobile ? '' : axisHtml}
        ${strips}
      </div>
    </div>`;

  const html = `
    <div class="ttl-chart">
      <svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs><pattern id="ttl-trump" patternContentUnits="objectBoundingBox" width="1" height="1"><image href="${trumpIcon}" xlink:href="${trumpIcon}" width="1" height="1" preserveAspectRatio="xMidYMid slice"></image></pattern></defs></svg>
      ${legend}
      <div class="ttl-grid" style="gap:${gap}px;">
        ${isMobile ? '' : labelCol}
        ${scrollPane}
        <div class="ttl-swipe" aria-hidden="true">Swipe<svg width="18" height="12" viewBox="0 0 18 12" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"><path d="M1 6h14M11 1l5 5-5 5"></path></svg></div>
      </div>
    </div>`;

  container.html(html);

  // start scrolled to the default (March–August) window
  const scrollNode = container.select('.ttl-scroll').node();
  if (scrollNode) {
    scrollNode.scrollLeft = scale(viewStart);
  }

  // hide the swipe hint as soon as the user starts scrolling. The initial
  // programmatic scroll above can dispatch its scroll event asynchronously
  // (after this listener is attached), so ignore movement that matches the
  // initial position and only react to a real user swipe.
  const swipeNode = container.select('.ttl-swipe').node();
  if (scrollNode && swipeNode) {
    const initialScroll = scrollNode.scrollLeft;
    const hideSwipe = () => {
      if (Math.abs(scrollNode.scrollLeft - initialScroll) < 2) return;
      swipeNode.style.opacity = '0';
      scrollNode.removeEventListener('scroll', hideSwipe);
    };
    scrollNode.addEventListener('scroll', hideSwipe, { passive: true });
  }

  // vertically center the swipe hint between the data lanes (the row SVGs,
  // excluding names/axis) — works for both the desktop and mobile layouts
  const gridNode = container.select('.ttl-grid').node();
  const laneNodes = container.selectAll('.ttl-strip svg').nodes();
  if (gridNode && swipeNode && laneNodes.length) {
    const gridTop = gridNode.getBoundingClientRect().top;
    const first = laneNodes[0].getBoundingClientRect();
    const last = laneNodes[laneNodes.length - 1].getBoundingClientRect();
    swipeNode.style.top = `${((first.top + last.bottom) / 2) - gridTop}px`;
  }

  if (window.pymChild) {
    window.pymChild.sendHeight();
  }
}
